import { createContext, useContext, useState, useEffect, useCallback, useRef, FC, PropsWithChildren } from "react";
import zlib from 'pako';
import { AttachDetails, ConflictDetails, IBuildSession, InitialiseV2Session, Item, LoadInvalidDetails, StartBuildSession, UpdateSummary, SessionOptions, ConflictResolution } from "@zerolight-core/generic-buildsession";
import { getInitialUrlArgs } from "./UrlArgsProvider";
import { SessionV2 } from "@zerolight-core/generic-buildsession/build/lib/buildSession";
import { Model } from "./data/models";
import { useAnalytics } from "./AnalyticsProvider";
import { load } from "./iframe-comms";
import { ExternalMessages, Messages, StringProvider } from "./StringProvider";
import { PartialFrameConfig } from "@zerolight-core/libzl";
import { CustomWrap, ModelProxy } from "./types/VisualizerTypes";
import { OpaqueBuildData } from "./helpers";
import { StarterPackWrap } from "./store/reducers/visualizer";

export const TopLevelConfigurationOptions = 3;
export const UseAutoResolve = true;
export const IgnoredOptions:string[] = [];

export interface ItemWithCustomData extends Item {
	customData: {
		prcodes: string[]
		tags: { id: string, name: string, sort: number }[]
		featureCamera?: string
		proxyModel?: ModelProxy
		thumbnail: string,
		thumbnailSummary: string,
		thumbnailPreview: string,
		thumbnailShape: string,
		thumbnailPRCodes: string[],
		subcategory: string[],
		wrap?: CustomWrap,
		incompatibleWith?: string[],
		requires?: string[],
		hideInSummary?: boolean,
		shortName?: string
	}
}

export interface CustomUnitData {
	prcodes?: string[];
	thumbnails: {
		id: string,
		url?: string,
		frameConfig?: PartialFrameConfig
	}[];
	templates: { id: string, includes: string[], thumbnail: string, thumbnailPreview: string, prcodes: string[], customWrap?: CustomWrap }[];
	tags: { id: string, camera: string, thumbnailShape: string }[];
}


export interface Conflict {
	session: IBuildSession
	details: ConflictDetails
	resolved: boolean
}

export interface MasterModel {
	manufacturer: string
	model: string
	year: string
}


export interface IBuildSessionContext {	
	userId: string
	buildId?: string

	current?: IBuildSession
	preview?: IBuildSession
	conflict?: Conflict
	clearConflict?: () => void
	undo?: () => void
	redo?: () => void
	lastChange?: UpdateSummary
	lastChangeAt?: number
	masterModel?: MasterModel

	templates?: Model[]
	initialConfigData?: Partial<OpaqueBuildData>
	errorLoadingShortcode?: boolean
}

let userId = window.localStorage.getItem("UserID")
if(!userId) {
	userId = crypto.randomUUID()
	window.localStorage.setItem("UserID", userId)
}

const BuildSessionContext = createContext<IBuildSessionContext>({userId});

// eslint-disable-next-line react-refresh/only-export-components
export const useBuildSession = ()  => useContext(BuildSessionContext)

interface IProps extends PropsWithChildren {
	modelYear?: string
	platformId?: string
	packageId?: string
	unitId?: string
	requiredFeatures?: string[]
	desiredFeatures?: string[]
	desiredAccessories?: string[]

	code?: string
}

class UndoRedoStack<T> {
	private m_stack: T[] = []
	private m_current = -1;

	public current() : T | undefined {
		if(this.m_current >= 0) {
			return this.m_stack[this.m_current]
		}
		return undefined
	}
	
	public push(value: T) {
		this.m_current += 1
		this.m_stack[this.m_current] = value
		this.m_stack.length = this.m_current + 1
	}

	public clear() {
		this.m_stack = []
		this.m_current = -1;
	}

	public canUndo() {
		return this.m_current > 0
	}

	public undo() {
		if(this.canUndo()) {
			this.m_current -= 1
		}
	}

	public canRedo() {
		return this.m_current < (this.m_stack.length - 1)
	}

	public redo() {
		if(this.canRedo()) {
			this.m_current += 1
		}
	}
}

interface State {
	id: string
	state: IBuildSession
	update?: UpdateSummary
	at: number
}

const MasterModel : MasterModel = {
	manufacturer: "slate",
	model: "slate700",
	year: "2025"
}

type BuildSessionSelection = { id: string, featureId: string, explicit: boolean, category: string, locked: number, quantity: number, attached: string[], accessorysku: string}[];

async function healState(state: Blob)
{
	if (state)
	{
		const stateObj = JSON.parse(await state.text()) as { selected: BuildSessionSelection };
		console.debug("State to heal", stateObj);
		
		if (stateObj.selected)
		{
			if (stateObj.selected.some(s => s.id == "SUV_COVER_OFF") && stateObj.selected.some(s => s.id == "TB009-SUV"))
			{
				stateObj.selected = stateObj.selected.filter(s => s.id != "TB009-SUV")
			}

			if (stateObj.selected.some(s => s.id == "FASTBACK_COVER_OFF"))
			{
				stateObj.selected.filter(s => s.id == "TB009-Fastback").forEach(s => s.id = s.accessorysku = "SUV_COVER_OFF");
				stateObj.selected = stateObj.selected.filter(s => s.id != "FASTBACK_COVER_OFF")
			}

			const replace = {
			
				"SUV_DOORS_OFF": "DOORS_OFF",
				"FASTBACK_DOORS_OFF": "DOORS_OFF",
			}

			stateObj.selected.filter(s => s.id in replace).forEach(s => s.id = s.accessorysku = replace[s.id as keyof typeof replace]);

			state = new Blob([JSON.stringify(stateObj)], { type: "application/zerolight-build" });
			console.debug("State after heal", stateObj);
		}
	}
	return state;
}

function sortResolutions(a: ConflictResolution, b: ConflictResolution, buildSession: IBuildSession){
	if(buildSession) {
		const aWeight = a.add.length + a.remove.filter(item => buildSession.state.selected.find((s: any) => s.id == item.id)?.explicit).length * 1.1;
		const bWeight = b.add.length + b.remove.filter(item => buildSession.state.selected.find((s: any) => s.id == item.id)?.explicit).length * 1.1;
		return aWeight - bWeight;
	}
	return 0;
}

//const AutoResolve = false
export const BuildSessionProvider: FC<IProps> = ({ children }) =>
{
	const {locale, overrideDate, shortcode, shortcodeService, configId, starterPack, appdata: overrideAppData} = getInitialUrlArgs()
	const steps = useRef(new UndoRedoStack<State>())
	//const blockAutoResolve = useRef(false)
	const [initialShortcode, setInitialShortcode] = useState(shortcode)
	const [session, setSession] = useState<SessionV2>()
	const [buildId, setBuildId] = useState<string>()
	const [preview, setPreview] = useState<IBuildSession>()
	const [current, setCurrent] = useState<State>()
	const [conflict, setConflict] = useState<Conflict | undefined>()
	const [strings, setStrings] = useState<ExternalMessages>({fetch: async () => {return {} as Messages}})
	const [templates, setTemplates] = useState<Model[]>()
	const [initialData, setInitialData] = useState<Blob | unknown>()
	const [masterModel, setMasterModel] = useState<MasterModel>()
	const [initialConfigData, setInitialConfigData] = useState<Partial<OpaqueBuildData>>()
	const {record} = useAnalytics()

	const onAutoFixConflict = useCallback((details: ConflictDetails, buildSession: IBuildSession) =>
	{
		details.resolutions.sort((a, b) => sortResolutions(a, b, buildSession))
		console.warn("Conflict", details, details.resolutions);

		for (const solution of details.resolutions)
		{
			if (IgnoredOptions.some(id => solution.add.some(item => item.id == id)))
				continue;

			solution.apply();
			return;
		}

		details.resolutions[0].apply()
	}, [])

	/*
	const onConflict = useCallback((details: ConflictDetails) =>
	{
		console.warn("Conflict", details);
		const originalState = steps.current.current()!.state
		let resolved = false;
		
		if(AutoResolve && !blockAutoResolve.current) {
			details.resolutions[0].apply()
			resolved = true
		}
		setConflict({session: originalState, details, resolved})
	}, [])
	*/
	const onAttachChoice = useCallback((details: AttachDetails) => {
		details.resolutions[0].apply()
	}, [])

	const onLoadInvalid = useCallback((details: LoadInvalidDetails) => {
		console.warn(`Fixed invalid load of ${details.package}\n\tAdded: ${details.added}\n\tRemoved: ${details.removed}`)
	}, [])

	/*
	useEffect(() => {
		if(current) {
			//Auto add defaults

			setPreview(current.state.clone(setPreview, onAutoFixConflict, onAttachChoice, onLoadInvalid))
		}
	}, [current, onAutoFixConflict, onAttachChoice, onLoadInvalid])
	*/

	const onBuildSessionUpdate = useCallback((state: IBuildSession, _?: boolean, update?: UpdateSummary) =>
	{
		console.log("BSUpdate", state);
		IgnoredOptions.length = 0;
		const current = {
			id: crypto.randomUUID(),
			state,
			update,
			at: Date.now()
		}
		steps.current.push(current)
		setCurrent(current)
		setConflict(undefined)
	}, [])

	/*Undo/Redo not currently compatible with preview concept - need to snapshot preview state too*/
	const undo = undefined /*useCallback(() => {
		if(steps.current.canUndo()) {
			steps.current.undo()
			setCurrent(steps.current.current())
		}
	}, [])*/

	const redo = undefined /*useCallback(() => {
		if(steps.current.canRedo()) {
			steps.current.redo()
			setCurrent(steps.current.current())
		}
	}, [])*/

	const initialise = useCallback(async () => {
		console.log("Initialise", locale)
		
		const appId = "slate"
		const bundlerDomain = (location.hostname.endsWith(".slate.zlthunder.net") || overrideAppData) ? "https://contentbundle-live.slate.zlthunder.net": undefined;
		const session = await InitialiseV2Session(appId, userId, ".", overrideAppData, bundlerDomain)
		const [language, country] = locale.split("-")
		const sessionOptions : SessionOptions = {
			country,
			language
		}

		
		await session.loadLocale(sessionOptions)
		setSession(session)

		const stringDataUrl = session.getResourceURL("slate/strings.json")
		const fetchStrings = async () => {
			const stringsRequest = await fetch(stringDataUrl)
			return await stringsRequest.json() as Messages
		}
		setStrings({fetch: fetchStrings})
	}, [locale, overrideAppData])

	useEffect(() => {initialise().catch(console.error).then(() => console.warn("BuildSessionProvider Initialized"))}, [initialise])

	const fetchSavedData = useCallback(async () => {
		if(!initialShortcode && !configId) {
			return
		}
		
		let buildData : OpaqueBuildData | undefined = undefined;
		if(initialShortcode) {
			const shortcodeResponse = await fetch(`https://shortcode-${shortcodeService}.omnistream.zlthunder.net/code/${initialShortcode}`)
			if(shortcodeResponse.ok) {
				const shortcodeData = await shortcodeResponse.json()
				if(shortcodeData.blob.state) {
					const data = atob(shortcodeData.blob.state)
					function str2ab(str: string) {
						const buf = new ArrayBuffer(str.length); // 2 bytes for each char
						const bufView = new Uint8Array(buf);
						for (let i=0, strLen=str.length; i < strLen; i++) {
							bufView[i] = str.charCodeAt(i);
						}
						return buf;
					}
					buildData = JSON.parse(zlib.inflate(str2ab(data), {to: 'string'})) as OpaqueBuildData;
				}
				else {
					buildData = shortcodeData.blob as OpaqueBuildData
				}
			}
			else {
				console.warn(`Ignoring unknown shortcode: ${initialShortcode} on ${shortcodeService}`)
				setInitialShortcode(undefined)
			}
		}
		else if(configId) {
			buildData = await load(configId) as unknown as OpaqueBuildData
		}
		setInitialConfigData(buildData)
	}, [configId, initialShortcode, shortcodeService])

	useEffect(() => {
		fetchSavedData()
	}, [fetchSavedData])

	const fetchTemplateData = useCallback(async () => {
		if(session) {
			const templateDataUrl = session.getResourceURL("slate/templates.json")
			const templatesRequest = fetch(templateDataUrl)

			const templatesResponse = await templatesRequest
			const templates = await templatesResponse.json() as Model[]
			setTemplates(templates)
		}
	}, [session])

	useEffect(() => {fetchTemplateData()}, [fetchTemplateData])

	useEffect(() => {
		if(templates?.length) {
			const template = templates?.find(template => template.id.toLowerCase() == starterPack?.toLowerCase()) ?? templates[0]
			if(template && !(initialShortcode || configId)) {
				const initialConfigData : Partial<OpaqueBuildData> = {
					prcodes: template.prcodes,
				}
				if(template.customWrap) {
					initialConfigData.customWraps = {[StarterPackWrap]: {...template.customWrap, selected: true, created: Date.now()}}
				}
				setInitialConfigData(initialConfigData)
			}
		}
	}, [configId, initialShortcode, starterPack, templates])

	const getInitialData = useCallback(async () => {
		if(!session || (starterPack && !templates) || ((initialShortcode || configId) && !initialConfigData)) {
			return
		}
		
		let initialData = undefined;

		const DefaultMasterModel = {
			manufacturer: "slate",
			model: "truck",
			year: "2025"
		}
		
		let desiredOptions = undefined
		
		if(initialConfigData?.session) {
			initialData = await healState(new Blob([initialConfigData.session], { type: "application/zerolight-build" }));
			const masterModel = initialConfigData.masterModel ?? DefaultMasterModel
			setMasterModel(masterModel)
			record?.(["Configurator", initialShortcode ? "LoadShortcode" : "LoadSavedBuild"], {initialShortcode, CreatorID: initialConfigData.creator})
		}
		else if(starterPack) {
			const template = templates?.find(template => template.id.toLowerCase() == starterPack.toLowerCase()) ?? templates?.[0]
			if(template) {
				desiredOptions = template.includes
			}
		}
		
		if(!initialData) {
			setMasterModel(DefaultMasterModel)
			initialData = {
				...DefaultMasterModel,
				pkg: "truck",
				desiredOptions
			}
			await session.loadTrimData(initialData.manufacturer, initialData.model, initialData.year)
		}
		setInitialData(initialData)
	}, [session, starterPack, templates, initialShortcode, configId, initialConfigData, record])
	useEffect(() => { getInitialData().catch(console.error) }, [getInitialData])
		
	const startBuild = useCallback(async () => {
		if(session && initialData) {
			//console.error("BuildStarted", session, initialData, overrideDate)
			steps.current.clear()
			setCurrent(undefined)
			setPreview(undefined)

			const buildId = crypto.randomUUID()
			setBuildId(buildId)
			let localBuildSession : IBuildSession | undefined
			const updateBuildSession = (buildSession : IBuildSession) => {
				localBuildSession = buildSession
			}
			localBuildSession = await StartBuildSession(session.userId, buildId, updateBuildSession, onAutoFixConflict, onAttachChoice, onLoadInvalid, initialData, overrideDate)

			if(localBuildSession) {	
				const pricing = localBuildSession.getFullSummary()?.pricing
				const items = (localBuildSession.getAllItems() as ItemWithCustomData[]).filter(i => i.selected)
				const configuration = items.filter(item => item.attachedPoints?.length).map(i => i.id)
				const expandedBasket = items.map(i => ({
					id: i.id,
					name: i.analyticsName ?? i.name,
					category: i.category,
					quantity: i.selected
				}))
				record?.(["Configurator", "BuildStarted"], {basket: expandedBasket, configuration, totalPrice: pricing?.totalPrice})

				const initialWrap = Object.values(initialConfigData?.customWraps ?? {}).find(w => w.selected)
				console.log(`Initial configuration:\n\t${configuration.join(",")}\n\t${initialWrap ? `D(${initialWrap.diffuse.r}:${initialWrap.diffuse.g}:${initialWrap.diffuse.b})__S(${initialWrap.specular.r}:${initialWrap.specular.g}:${initialWrap.specular.b}) - ${initialWrap.finish}` : ""}`)

				const current = {
					id: crypto.randomUUID(),
					state: localBuildSession.clone(onBuildSessionUpdate, onAutoFixConflict, onAttachChoice, onLoadInvalid),
					update: undefined,
					at: Date.now()
				}
				steps.current.push(current)
				setCurrent(current)
				setPreview(localBuildSession.clone(setPreview, onAutoFixConflict, onAttachChoice, onLoadInvalid))
			}
		}
	}, [initialData, onAttachChoice, onAutoFixConflict, onBuildSessionUpdate, onLoadInvalid, overrideDate, record, session])

	useEffect(() => {startBuild().catch(console.error)}, [startBuild])

	const clearConflict = useCallback(() => {
		setConflict(undefined)
	}, [])

	//Could add undo/redo keyboard listeners?

	const state : IBuildSessionContext = {
		userId: session?.userId ?? "",
		buildId,
		current: current?.state,
		preview,
		conflict: conflict,
		lastChange: current?.update,
		lastChangeAt: current?.at,
		undo: steps.current.canUndo() ? undo : undefined,
		redo: steps.current.canRedo() ? redo : undefined,
		clearConflict,
		templates,
		masterModel,
		initialConfigData,
		errorLoadingShortcode: shortcode ? initialShortcode !== shortcode : undefined
	}
	
	return (
		<StringProvider data={strings} locale={locale}>
			<BuildSessionContext.Provider value={state}>
				{children}
			</BuildSessionContext.Provider>
		</StringProvider>
	)
}
